import { useScreenSize } from '@/themes/media-query'
import { DataGrid } from 'devextreme-react'

import { useCallback, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  Column,
  ColumnChooser,
  Editing,
  Export,
  FilterRow,
  HeaderFilter,
  Item,
  Scrolling,
  Selection,
  StateStoring,
  Toolbar,
  DataGridTypes,
  MasterDetail,
  FilterPanel,
  DataGridRef,
  Search,
  Pager,
  Paging,
} from 'devextreme-react/data-grid'
import { EditorPreparingEvent, ExportingEvent, RowDblClickEvent } from 'devextreme/ui/data_grid'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { Workbook } from 'exceljs'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import { StatoTicket } from '@/routes/tickets/TicketEditor.enums'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import TicketBriefDetail from '@/routes/tickets/TicketBriefDetail'
import { columnSourceFactory, isUserRoleAllowed } from '@/routes/utils'
import DateColumn from '@/components/date-column/DateColumn'
import { ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { gridTotalItemsLabel } from '@/constants'
import Rating from '@/components/Rating/Rating'
import { Roles } from '@/auth/azure/Roles'
import { useLoaderData } from 'react-router-typesafe'
import { ticketsHistoricPageRouteLoader } from '@/routes/tickets/historic/TicketsHistoricPage.route'
import DocumentHead from '@/components/document-head/DocumentHead'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { Reparto } from '@/enums'
import { createRoot } from 'react-dom/client'
import { createHistoricBaseFilter } from '@/routes/tickets/historic/historicSearchFilter'
import { HISTORIC_GRID_STORAGE_KEY, SEARCH_PAGE_SIZE } from '@/routes/tickets/historic/historicGridState'
import { HistoricSearchToolbar, useHistoricSearchGrid } from '@/routes/tickets/historic/useHistoricSearchGrid'

const getLocalStorage = (): Storage | undefined => {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

const TicketsHistoricPage = () => {
  const { userInfo } = useLoaderData<typeof ticketsHistoricPageRouteLoader>()
  const title = 'Storico Ticket'
  const token = useTokenRefresh()
  const navigate = useNavigate()
  const gridRef = useRef<DataGridRef>(null)
  const currentScreenSize = useScreenSize()
  // const [ticketsHistoricDataSource, setTicketsHistoricDataSource] = useState<ticket[]>([])

  const getGridHeight = useCallback(() => {
    let h = '79vh'
    if (currentScreenSize.isMedium) {
      h = '75vh'
    } else if (currentScreenSize.isSmall) {
      h = '100vh'
    } else if (currentScreenSize.isXSmall) {
      h = '65vh'
    }
    return h
  }, [currentScreenSize])

  const ExportDataGridToExcel = (component: any) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('Tickets')
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'HistoricTickets.xlsx')
      })
    })
  }

  const onExporting = (e: ExportingEvent) => {
    ExportDataGridToExcel(e.component)
  }

  const cellRenderRating = (cellData: DataGridTypes.ColumnCellTemplateData) => {
    return cellData.value && cellData.value > 0 ? <Rating value={cellData.value} /> : <></>
  }

  // Historic scope used by the column header filters; unchanged.
  const initialFilterValue = useMemo(() => createHistoricBaseFilter(), [])

  // Committed native search, optional 'Totale e gruppi' mode, request guards and layout storing.
  const {
    isSearchMode,
    mode,
    gridKey,
    countRequested,
    hasToken,
    dataSource: ticketsHistoricDataSource,
    stateStoring,
    gridModeOptions,
    commitSearch,
    searchDraft,
    toggleCount,
  } = useHistoricSearchGrid({
    host: import.meta.env.VITE_QSADMINAPI_HOST,
    token,
    gridRef,
    storage: getLocalStorage(),
    configure: ODataStoreRequestConfiguration,
  })

  return (
    <>
      <h2 className={'content-block'}>{title}</h2>
      <div className={'content-block'}>
        <HistoricSearchToolbar
          isXSmall={currentScreenSize.isXSmall}
          isSearchMode={isSearchMode}
          countRequested={countRequested}
          searchDraft={searchDraft}
          onToggle={toggleCount}
          onCommit={commitSearch}
        />

        {hasToken && (
          <DataGrid
            // A new instance per mode, committed search and counted session: its first load waits
            // for the stored layout of that mode, so no request is ever sent with the previous
            // grouping/paging/count.
            key={gridKey}
            id={'tickets-historic-datagrid'}
            height={getGridHeight()}
            noDataText="Nessun ticket trovato"
            className={'dx-card wide-card'}
            showBorders={false}
            dataSource={ticketsHistoricDataSource}
            showColumnLines={true}
            wordWrapEnabled={false}
            showRowLines={true}
            rowAlternationEnabled={true}
            allowColumnResizing={true}
            allowColumnReordering={true}
            width="100%"
            onExporting={onExporting}
            columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
            ref={gridRef}
            remoteOperations={gridModeOptions.remoteOperations}
            grouping={gridModeOptions.grouping}
            groupPanel={gridModeOptions.groupPanel}
            customizeColumns={gridModeOptions.customizeColumns}
            onRowDblClick={(e: RowDblClickEvent<any, any>) => {
              if (e.rowType === 'data') window.open(`/tickets/${e.data.id}`, '_blank')
            }}
            onEditorPreparing={(e: EditorPreparingEvent) => {
              if (e.parentType === 'filterRow' && e.dataField === 'rating') {
                e.editorName = 'dxSelectBox'
                e.editorOptions.showClearButton = true
                e.editorOptions.dataSource = [1, 2, 3, 4, 5]
                e.editorOptions.itemTemplate = (data: any) => {
                  const container = document.createElement('div')
                  const root = createRoot(container)
                  root.render(<Rating value={data} />)
                  return container
                }
              }
            }}
          >
            <Pager
              visible={gridModeOptions.pagerVisible}
              allowedPageSizes={'auto'}
              displayMode={'compact'}
              showPageSizeSelector={false}
              showInfo={true}
              showNavigationButtons={false}
              infoText={gridTotalItemsLabel}
            />
            <FilterRow visible={true} />
            <Export enabled={true} />
            <Editing allowAdding={true} />
            <HeaderFilter visible={true} />
            <FilterPanel visible={true} />
            <Selection mode="single" />
            <ColumnChooser enabled={true} />
            <MasterDetail enabled={true} component={TicketBriefDetail} />,
            {gridModeOptions.searchPaging && <Paging pageSize={SEARCH_PAGE_SIZE} />}
            <Scrolling mode={gridModeOptions.scrollingMode} />
            <StateStoring
              enabled={true}
              type="custom"
              storageKey={HISTORIC_GRID_STORAGE_KEY}
              customLoad={stateStoring.customLoad}
              customSave={stateStoring.customSave}
              savingTimeout={50}
            />
            <Column type="buttons" width={'2.5%'} alignment="left"></Column>
            <Column
              dataField={'id'}
              visible={false}
              caption="ID"
              width={'auto'}
              dataType="number"
              defaultSortOrder="desc"
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
              }
            >
              <HeaderFilter
                dataSource={columnSourceFactory(token, 'ticket', 'id', true, initialFilterValue)}
              ></HeaderFilter>
            </Column>
            <Column
              dataField={'rating'}
              caption={'RATING'}
              cellRender={cellRenderRating}
              minWidth={70}
              width={70}
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
              }
            >
              <HeaderFilter
                dataSource={[
                  {
                    text: '0',
                    value: 0,
                  },
                  {
                    text: '1',
                    value: 1,
                  },
                  {
                    text: '2',
                    value: 2,
                  },
                  {
                    text: '3',
                    value: 3,
                  },
                  {
                    text: '4',
                    value: 4,
                  },
                  {
                    text: '5',
                    value: 5,
                  },
                ]}
              ></HeaderFilter>
            </Column>
            <Column
              dataField={'ded_Dis'}
              cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
                const data: ticket = cellData.row.data
                return (
                  <ObjectCodeLink
                    url={`../${data.id}`}
                    text={data.ded_Dis}
                    disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
                  ></ObjectCodeLink>
                )
              }}
              caption={'TICKET ID'}
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
              }
            >
              <HeaderFilter dataSource={columnSourceFactory(token, 'ticket', 'ded_Dis', true, initialFilterValue)}>
                <Search enabled={true} searchExpr={'ded_Dis'} />
              </HeaderFilter>
            </Column>
            <Column
              dataField={'sede.azienda.nome'}
              cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
                const data: ticket = cellData.row.data
                return (
                  <ObjectCodeLink
                    url={`/clients/${data.sede?.azienda?.id}`}
                    text={data.sede?.azienda?.nome}
                  ></ObjectCodeLink>
                )
              }}
              caption={'AZIENDA'}
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 16 : undefined
              }
            >
              <HeaderFilter dataSource={columnSourceFactory(token, 'azienda', 'nome')}>
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            </Column>
            <Column
              dataField={'sede.nome'}
              caption={'SEDE'}
              allowHeaderFiltering={false}
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
              }
            />
            <Column
              dataField={'leg_contatto'}
              caption={'CONTATTO'}
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 15 : undefined
              }
            >
              <HeaderFilter
                dataSource={columnSourceFactory(token, 'ticket', 'leg_contatto', undefined, initialFilterValue)}
              >
                <Search enabled={true} searchExpr={'leg_contatto'} />
              </HeaderFilter>
            </Column>
            <Column
              dataField={'creatore.fullname'}
              caption={'CREATORE'}
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
              }
            >
              <HeaderFilter dataSource={columnSourceFactory(token, 'user', 'fullname')}>
                <Search enabled={true} searchExpr={'fullname'} />
              </HeaderFilter>
            </Column>
            {DateColumn({
              dataField: 'apertura',
              caption: 'INIZIO',
              format: 'dd/MM/yyyy HH:mm',
              width: 'auto',
              hidingPriority:
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 14 : undefined,
            })}
            {DateColumn({
              dataField: 'chiusura',
              caption: 'FINE',
              format: 'dd/MM/yyyy HH:mm',
              width: 'auto',
              hidingPriority:
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 13 : undefined,
            })}
            <Column
              dataField="stato.nome"
              caption="STATO"
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined
              }
            >
              <HeaderFilter
                dataSource={columnSourceFactory(token, 'stato_ticket', 'nome', undefined, [
                  [['id', StatoTicket.COMPLETATO], 'or', ['id', StatoTicket.ELIMINATO]],
                ])}
              >
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            </Column>
            <Column
              dataField="assegnato.fullname"
              caption="ASSEGNATO"
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined
              }
            >
              <HeaderFilter
                dataSource={columnSourceFactory(token, 'user', 'fullname', undefined, [
                  [['reparto/id', Reparto.TECNICO], 'or', ['reparto/id', Reparto.SVILUPPO]],
                ])}
              >
                <Search enabled={true} searchExpr={'fullname'} />
              </HeaderFilter>
            </Column>
            <Column
              dataField="tipologia_ticket.nome"
              caption="CATEGORIA"
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
              }
            >
              <HeaderFilter dataSource={columnSourceFactory(token, 'tipologia_ticket', 'nome')}>
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            </Column>
            <Column
              dataField="durata"
              caption="DURATA"
              dataType="number"
              allowHeaderFiltering={false}
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 11 : undefined
              }
            >
              <HeaderFilter
                dataSource={columnSourceFactory(token, 'ticket', 'durata', true, initialFilterValue)}
              ></HeaderFilter>
            </Column>
            <Column
              dataField="piattaforma.nome"
              caption="PIATTAFORMA"
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 10 : undefined
              }
            >
              <HeaderFilter dataSource={columnSourceFactory(token, 'piattaforma_ticket', 'nome')}>
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            </Column>
            <Column
              dataField="applicazione.nome"
              caption="SOFTWARE QS"
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 9 : undefined
              }
            >
              <HeaderFilter dataSource={columnSourceFactory(token, 'qs_supporto_applicazione', 'nome')}>
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            </Column>
            <Column
              dataField="tipologia_problema.nome"
              caption="PROBLEMA"
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
              }
            >
              <HeaderFilter dataSource={columnSourceFactory(token, 'tipologia_problema', 'nome')}>
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            </Column>
            <Column
              dataField="argomento_problema.nome"
              caption="ARGOMENTO"
              hidingPriority={
                currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
              }
            >
              <HeaderFilter dataSource={columnSourceFactory(token, 'argomento_problema', 'nome')}>
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            </Column>
            <Toolbar>
              {!currentScreenSize.isXSmall && <Item name="groupPanel" />}
              <Item
                name="columnChooserButton"
                locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
                showText="inMenu"
                options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
              />
              <Item
                name="exportButton"
                locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
                showText="inMenu"
                options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
              />
            </Toolbar>
          </DataGrid>
        )}
        {mode === 'search' && (
          // Search results load without an exact total; nothing here claims more or fewer results.
          <div style={{ padding: '6px 12px', fontSize: 13, opacity: 0.8 }}>Totale non calcolato</div>
        )}
      </div>
    </>
  )
}

export default TicketsHistoricPage
